import React, { useEffect, useState } from 'react';
import { Alert, Container, Modal, Spinner, Button, Row, Col, Form } from 'react-bootstrap';
import {
    getBookingDetails,
    useBookingState,
    useAccountState,
    getTripSettings,
    getVehicleAssignments,
    useVehicleAssignmentsState,
    useInvoicesState,
    getInvoiceDetails,
    getBookingVehiclesDetails,
} from '../../../../Context';
import { formatDateTimeWithDayAbbr, convertQueryStringToObject } from '../../../../utils/helper';
import { bookingConfirmation } from '../../../../services/bookingsService';
import { getCustomerDetails } from '../../../../config';
import Loading from '../../../../Components/Loading';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Swal from 'sweetalert2';
import './style.scss';
import { Link } from 'react-router-dom';
dayjs.extend(utc);
dayjs.extend(timezone);

const BookingDetails = (props) => {
    const bookingId = props.match.params.id;
    const queryObj = convertQueryStringToObject(props.location.search);
    const {
        bookingsDispatch,
        bookings: { bookingDetails, addonAssignments, dispatches },
    } = useBookingState();
    const {
        invoiceDispatch,
        invoice: { invoiceDetails, errorMessage },
    } = useInvoicesState();
    const {
        AccountsDispatch,
        tripSettings: { tripSettings },
    } = useAccountState();
    const [loading, setLoading] = useState(false);
    const { vehicleAssignmentsDispatch } = useVehicleAssignmentsState();
    const [bookingVehicleAssignments, setBookingVehicleAssignments] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await getTripSettings(AccountsDispatch);
            const bookingData = await getBookingDetails(bookingsDispatch, bookingId);
            const data = await getVehicleAssignments(vehicleAssignmentsDispatch, bookingId);
            await getBookingVehiclesDetails(bookingsDispatch, bookingId);
            setBookingVehicleAssignments(data);
            if (bookingData?.details.invoiceID) {
                await getInvoiceDetails(invoiceDispatch, bookingData.details.invoiceID);
            }

            setLoading(false);
        })();
    }, [bookingsDispatch, bookingId, AccountsDispatch, vehicleAssignmentsDispatch]);

    const onVehicleClick = (item) => {
        const originParams =
            queryObj.backTo && queryObj.backUrl
                ? `&originBackTo=${encodeURIComponent(queryObj.backTo)}&originBackUrl=${encodeURIComponent(queryObj.backUrl)}`
                : '';
        props.history.push(`/bookings-vehicle-assignment/${item.id}?bookingId=${bookingId}${originParams}`);
    };

    const handleConfirmDetails = async () => {
        const confirm = await Swal.fire({
            title: 'Confirm Booking Details',
            text: 'Are you sure you would like to confirm the details of this booking?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, confirm',
        });
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            const customer = getCustomerDetails();
            const customerName = `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
            await bookingConfirmation(bookingId, customerName);
            await getBookingDetails(bookingsDispatch, bookingId);
        } catch (error) {
            console.error('Error confirming booking details', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-100 content-section">
            <div className="px-3 mt-2">
                <div style={{ display: 'grid', gap: '2px' }}>
                    <Button
                        onClick={() => {
                            props.history.push(queryObj.backUrl || `/bookings`);
                        }}
                        style={{ backgroundColor: `${tripSettings.buttonColor}`, borderColor: 'transparent' }}
                    >
                        <i className="fas fa-chevron-left" />{' '}
                        {queryObj.backTo ? `BACK TO ${queryObj.backTo.toUpperCase()}` : 'BACK TO BOOKINGS'}
                    </Button>
                    {bookingDetails?.amountOutstanding &&
                    bookingDetails.amountOutstanding > 0 &&
                    !bookingDetails.disablePayment &&
                    bookingDetails.paymentTermID &&
                    Number(bookingDetails.paymentTermID) === 1 ? (
                        <Button
                            onClick={() => {
                                window.open(
                                    `${window.location.origin}/p/booking-payment?bid=${bookingId}&ts=${new Date(bookingDetails?.createdAt).valueOf()}`,
                                    '_blank'
                                );
                            }}
                            variant="info"
                        >
                            MAKE PAYMENT
                        </Button>
                    ) : (
                        ''
                    )}
                    {bookingDetails.showDriverDetailsButton ? (
                        <Button
                            onClick={() =>
                                props.history.push(
                                    `/p/bookings/driver-details/${bookingId}?ts=${new Date(
                                        bookingDetails?.createdAt
                                    ).valueOf()}`
                                )
                            }
                            style={{ backgroundColor: `${tripSettings.buttonColor}`, borderColor: 'transparent' }}
                        >
                            DRIVER DETAILS
                        </Button>
                    ) : (
                        ''
                    )}
                    {!bookingDetails.cancelledAt &&
                    !bookingDetails.detailsConfirmedByCustomerAt &&
                    bookingDetails.showConfirmDetailsButton ? (
                        <Button
                            onClick={handleConfirmDetails}
                            style={{ backgroundColor: `${tripSettings.buttonColor}`, borderColor: 'transparent' }}
                        >
                            CONFIRM DETAILS
                        </Button>
                    ) : (
                        ''
                    )}
                </div>

                <div className="col-12 head-div mt-2" style={{ background: tripSettings?.sectionColor }}>
                    <div>
                        <span className="sg-card-lt">Trip Details</span>
                    </div>
                </div>
                <div className="col-12 ">
                    {bookingDetails.tripReference ? (
                        <span className="dtail">Trip Reference: {bookingDetails.tripReference}</span>
                    ) : null}
                    {bookingDetails.groupName ? (
                        <span className="dtail">Group Name: {bookingDetails.groupName}</span>
                    ) : null}
                    {bookingDetails.bookingQuoteId ? (
                        <span className="dtail">Associated Quote ID #: {bookingDetails.bookingQuoteId}</span>
                    ) : null}
                    {bookingDetails.purchaseOrder ? (
                        <span className="dtail">Purchase Order #: {bookingDetails.purchaseOrder}</span>
                    ) : null}
                    {bookingDetails.detailsConfirmedByCustomerName && bookingDetails.detailsConfirmedByCustomerAt ? (
                        <span className="dtail">
                            Details confirmed by <b>{bookingDetails.detailsConfirmedByCustomerName}</b> on{' '}
                            <b>{formatDateTimeWithDayAbbr(bookingDetails.detailsConfirmedByCustomerAt)}</b>
                        </span>
                    ) : null}
                </div>

                <div className="col-12 head-div mt-2" style={{ background: tripSettings?.sectionColor }}>
                    <div>
                        <span className="sg-card-lt">Financial</span>
                    </div>
                </div>
                <div className="col-12 ">
                    {bookingDetails?.holdInvoice !== 0 &&
                        bookingDetails?.displayHoldInvoiceMessage !== 0 &&
                        bookingDetails?.holdInvoiceText && (
                            <Alert variant="warning">{bookingDetails.holdInvoiceText}</Alert>
                        )}
                    {bookingDetails?.invoiceID ? (
                        <Row>
                            <Col>
                                <Form.Label>View Invoice :</Form.Label>
                                <Link to={`/invoices/${bookingDetails.invoiceID}`}>
                                    <span className="font-weight-bold ml-2">
                                        {invoiceDetails?.invoiceNumber}
                                        <i className="fa fa-link ml-2" />
                                    </span>
                                </Link>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            {bookingDetails.paymentStatus ? (
                                <span className="dtail">Payment Status: {bookingDetails.paymentStatus}</span>
                            ) : null}
                            {bookingDetails.depositDueDate ? (
                                <span className="dtail">Deposit Due Date: {bookingDetails.depositDueDate}</span>
                            ) : null}
                            {bookingDetails.depositAmount ? (
                                <span className="dtail">Deposit Amount: {bookingDetails.depositAmount}</span>
                            ) : null}
                            {bookingDetails.finalPaymentDueDate ? (
                                <span className="dtail">
                                    Final Payment Due Date: {bookingDetails.finalPaymentDueDate}
                                </span>
                            ) : null}
                            <span className="dtail">Total Before Tax: {bookingDetails.totalBeforeTax || 0}</span>
                            <span className="dtail">Total Tax: {bookingDetails.totalTax || 0}</span>
                            <span className="dtail">Amount Paid: {bookingDetails.amountPaid || 0}</span>
                            <span className="dtail">Total After Tax: {bookingDetails.totalAfterTax || 0}</span>
                            <span className="dtail">Amount Outstanding: {bookingDetails.amountOutstanding || 0}</span>
                        </>
                    )}
                </div>

                <div className="col-12 head-div mt-2" style={{ background: tripSettings?.sectionColor }}>
                    <div>
                        <span className="sg-card-lt">Routes</span>
                    </div>
                </div>
                {bookingVehicleAssignments.map((item) => (
                    <div
                        className="d-flex list-div mt-2"
                        onClick={() => {
                            onVehicleClick(item);
                        }}
                        style={{ height: 'auto', minHeight: 'fit-content' }}
                    >
                        <div className="col" style={{ minWidth: 0 }}>
                            <div className={`color-line`} style={{ background: tripSettings?.mainColor }}></div>
                            <div className="card-l">
                                <span className="card-lt">{item.bookingId}</span>
                                <span
                                    className="card-lb"
                                    style={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        display: 'block',
                                    }}
                                >
                                    {item.routeDescription}
                                </span>
                            </div>
                        </div>

                        <div className="col-auto text-right p-0 py-2 pr-3 align-self-center">
                            <div className="card-r">
                                <div className="mb-2">
                                    <span
                                        className="d-block font-weight-bold"
                                        style={{ fontSize: '0.7em', textTransform: 'uppercase' }}
                                    >
                                        Pick-Up
                                    </span>
                                    <span className="d-block small text-nowrap font-weight-bold text-dark">
                                        {formatDateTimeWithDayAbbr(item.firstDepartureTime)}
                                    </span>
                                </div>
                                <div>
                                    <span
                                        className="d-block font-weight-bold"
                                        style={{ fontSize: '0.7em', textTransform: 'uppercase' }}
                                    >
                                        Drop-Off
                                    </span>
                                    <span className="d-block small text-nowrap font-weight-bold text-dark">
                                        {formatDateTimeWithDayAbbr(item.lastArrivalTime)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {loading && <Loading loading={loading} />}

                {loading && (
                    <Container className="mb-5 text-center">
                        <Modal show={true} animation={false} className="loading-modal text-center">
                            <Spinner animation="border" variant="primary" />
                        </Modal>
                    </Container>
                )}
            </div>
        </div>
    );
};

export default BookingDetails;
